import Backdrop from '@mui/material/Backdrop';
import MuiModal, { ModalProps as MuiModalProps } from '@mui/material/Modal';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import ModalBodyPaper, {
  ModalBodyPaperProps as BodyProps,
} from './ModalBodyPaper';

const useStyles = makeStyles<{ fixedWidth?: boolean }>({
  name: 'Modal',
})((theme, { fixedWidth }) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto',
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
  },
  modalBody: {
    position: 'relative',
    // auto margin helps with vertical overflow
    // @see https://stackoverflow.com/a/33455342
    margin: 'auto',
    boxShadow: theme.shadows[5],
    '&:focus': {
      outline: 'none',
    },
    maxWidth: `calc(100vw)`,
    // spacing accounts for container gutter width, the content container will have minWidth 400
    [theme.breakpoints.up('sm')]: {
      minWidth: 400 + Number(theme.spacing(4).slice(0, -2)) * 2,
      width: fixedWidth
        ? 400 + Number(theme.spacing(4).slice(0, -2)) * 2
        : undefined,
    },
    [theme.breakpoints.down('sm')]: {
      width: `100vw`,
    },
  },
}));

const useContainerStyles = makeStyles({ name: 'ModalContainer' })(theme => ({
  container: {
    paddingLeft: theme.spacing(0),
    paddingRight: theme.spacing(0),
  },
}));

export interface ModalProps extends Omit<MuiModalProps, 'classes'> {
  open: boolean;
  onClose: () => void;
  onCloseComplete?: () => void;
  fixedWidth?: boolean;
  includePaper?: boolean;
  ModalBodyPaperProps?: BodyProps;
  classes?: {
    modalBody?: string;
  };
  children: any;
  disableGutters?: boolean;
}

/**
 * Modal
 *
 * When using Modal, please add the following child components for accessibilty:
 *    <div id="modal-title" role="dialog" aria-label="<modal title>" />
 *    <div id="modal-description" aria-label="<modal description>" />
 *
 * The Modal component is typically used with the `includePaper` and `includeDismissIcon` props.
 * These give the modal a consistent background (Paper), padding, and close/dismiss action.
 *
 * @argument {boolean?} fixedWidth If set, the modal body will be constrained to 400px + gutter width
 * @argument {boolean?} includePaper If set, include the paper background styling. The modal body
 *                      will be rendered within a Paper component.
 *
 * @example
 *    <Modal>
 *      <div id="modal-title" role="dialog" aria-label="<modal title>" />
 *      <div id="modal-description" aria-label="<modal description>" />
 *      {...}
 *    </Modal>
 *
 * @example
 *    // Disable the inclussion of dismiss icon:
 *    <Modal
 *      ModalBodyPaperProps={{
 *        includeDismissIcon: false
 *      }}
 *    />
 */
const Modal: React.FC<ModalProps> = props => {
  const {
    open,
    onClose,
    onCloseComplete,
    children,
    classes: incomingClasses = {},
    includePaper = true,
    ModalBodyPaperProps = { includeDismissIcon: true },
    BackdropComponent = Backdrop,
    fixedWidth,
    disableGutters = false,
    ...rest
  } = props;
  const { classes, cx } = useStyles({ fixedWidth });

  return (
    <MuiModal
      closeAfterTransition
      className={classes.modal}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      {...{ open, onClose, BackdropComponent, ...rest }}
    >
      <div className={cx(classes.modalBody, incomingClasses.modalBody)}>
        {includePaper ? (
          <ModalBodyPaper onClose={onClose} {...ModalBodyPaperProps}>
            {children}
          </ModalBodyPaper>
        ) : (
          children
        )}
      </div>
    </MuiModal>
  );
};

export default Modal;
